import { utility } from '@phi/components';
import './LoadDefaultObject.scss';
import { defineComponent, h, PropType, watch } from 'vue';
import { Option } from '@vaaas/fpts/option';
import { assoc } from '@vaaas/fpts/object';
import FormSelect from './FormSelect';
import { Length } from '../Units';
import { name_without_counter, recommend_different_filename } from '@phi/util/file-name';
import {
    FormText,
    Label,
    FormWithLabels,
    FormNumber,
} from '@components/form';
import { SimpleSelectDocumentElement } from '@components/OnshapeExplorer';
import { ID } from '@phi/newtype';
import { Document, OnshapeElement } from '@phi/onshape';
import * as API from '@phi/frontend/api/calls';
import { handle } from '@vaaas/fpts/result';
import { Record } from '@bloomberg/record-tuple-polyfill';
import { display_message_to_user, LOG } from '@phi/logging/frontend';
import { Unary } from '@vaaas/fpts/data';
import { disallowed_symbols, reject_symbols } from '../keyboard';
import { join } from '@vaaas/fpts/iter';
import { B } from '@vaaas/fpts/combinator';
import { Flex } from '@phi/components/layout';
import { PositiveNumber } from '@phi/validation';

const FormSelectUnits = FormSelect<Length>();

const img_cube_png = 'img/default_models/cube.png';
const img_rounded_cube_png = 'img/default_models/rounded_cube.png';
const img_cube_with_hole_png = 'img/default_models/cube_with_hole.png';
const img_square_png = 'img/default_models/square.png';
const img_cylinder_png = 'img/default_models/cylinder.png';
const img_rounded_cylinder_png = 'img/default_models/rounded_cylinder.png';
const img_cylinder_with_hole_png = 'img/default_models/cylinder_with_hole.png';
const img_circle_png = 'img/default_models/circle.png';

/** the available default models that can be selected */
type Model =
    'cube'
    | 'cube_with_hole'
    | 'square'
    | 'cylinder'
    | 'cylinder_with_hole'
    | 'circle'

/** the type of the prop that we will be operating with */
type Value = {
    selectedModel: Model;
    units: Length;
    x: number;
    y: number;
    z: number;
    height: number;
    diameter: number;
    file_name: string;
}

export default defineComponent({
    props: {
        defaultUnit: {
            required: false,
            type: String as PropType<Length>,
            default: Length.cm,
        },

        load_into: {
            required: false,
            type: Boolean,
            default: false,
        },

        value: {
            required: true,
            type: null as any as PropType<Option<Value>>,
        },

        standalone: {
            required: true,
            type: Boolean,
        },

        document: {
            required: true,
            type: null as any as PropType<Option<ID<'document'>>>,
        },

        workspace: {
            required: true,
            type: null as any as PropType<Option<ID<'workspace'>>>,
        },

        api: {
            required: false,
            type: Object as PropType<typeof API>,
            default: () => API,
        },

        had_selected_document: {
            required: true,
            type: Boolean,
        },
    },

    data() {
        return {
            documents: [] as Document[],
            elements: [] as OnshapeElement[],
        };
    },

    emits: {
        'update:value': (x: Value) => true,
        'update:document': (x: Option<ID<'document'>>) => true,
        'update:workspace': (x: Option<ID<'workspace'>>) => true,
    },

    computed: {
        existing_element_names(): string[] {
            return this.elements.map(x => x.name.slice(0, -4));
        },

        bad_file_name(): boolean {
            if (this.value)
                return this.existing_element_names.includes(this.value.file_name);
            else
                return false;
        },

        /** calculates a sensible default file name */
        default_name(): string {
            return this.existing_element_names.includes(default_file_name)
                ? recommend_different_filename(default_file_name, this.existing_element_names)
                : default_file_name;
        },

        /** the current filename without its counter suffix
         *
         * Name (1) -> Name
         */
        filename_without_counter(): string {
            return this.value
                ? name_without_counter(this.value.file_name)
                : default_file_name;
        },

        /** a sensible default value that is not undefined */
        default_value(): Value {
            return {
                selectedModel: 'cube',
                units: this.defaultUnit,
                x: 1,
                y: 1,
                z: 1,
                height: 1,
                diameter: 1,
                file_name: this.default_name,
            };
        },

        selected_document: {
            get(): { document: Option<ID<'document'>>; workspace: Option<ID<'workspace'>> } {
                return Record({ document: this.document, workspace: this.workspace });
            },

            set(x: { document: Option<ID<'document'>>; workspace: Option<ID<'workspace'>> }) {
                this.$emit('update:document', x.document);
                this.$emit('update:workspace', x.workspace);
            },
        }
    },

    watch: {
        existing_element_names(value: typeof this.existing_element_names) {
            if (!this.bad_file_name) return;
            else if (!this.value) return;
            else if (this.existing_element_names.includes(this.value.file_name))
                this.update_value_field(
                    'file_name',
                    recommend_different_filename(this.filename_without_counter, this.existing_element_names)
                );
            else return;
        },

        defaultUnit(value: typeof this.defaultUnit) {
            if (this.value)
                this.update_value_field('units', value);
        },

        value(value: Option<Value>) {
            if (value && this.existing_element_names.includes(value.file_name))
                this.update_value_field('file_name', recommend_different_filename(this.filename_without_counter, this.existing_element_names));
        },
    },

    methods: {
        /** helper function for updating the passed value */
        update_value_field<K extends keyof Value>(k: K, v: Value[K]) {
            const o: Value = this.value ?? this.default_value;
            this.$emit('update:value', assoc(k)(v)(o));
        },
    },

    created() {
        watch(
            () => [this.document, this.workspace] as const,
            async ([document, workspace]) => {
                if (document && workspace)
                    this.elements = await this.api.listDocumentElements(document, workspace, false)
                        .then(handle(() => [] as OnshapeElement[]));
            }
        );
    },

    async mounted() {
        if (!this.standalone)
            this.elements = await this.api.listDocumentElements(this.document!, this.workspace!, false)
                .then(handle(() => [] as OnshapeElement[]));
    },

    render() {
        return h('div', { class: [
            'load-default-object',
            utility.margin_gap_3,
        ] }, [
            h(Flex, { direction: 'row' }, () => [
                ModelSelector({
                    value: this.value?.selectedModel,
                    onUpdate: (x) => this.update_value_field('selectedModel', x)
                }),

                h(FormWithLabels, {
                    gap: 'tight',
                    width: '5em',
                    class: {
                        hidden: this.value?.selectedModel === undefined,
                        dimensions: true,
                    },
                }, () => [
                    UnitSelector({
                        value: this.value?.units,
                        onUpdate: (x) => this.update_value_field('units', x),
                    }),

                    DimensionSelector({
                        label: 'X',
                        hidden: !['cube', 'cube_with_hole', 'square'].includes(this.value?.selectedModel as any),
                        value: this.value?.x,
                        onUpdate: x => this.update_value_field('x', x),
                    }),

                    DimensionSelector({
                        label: 'Y',
                        hidden: !['cube', 'cube_with_hole', 'square'].includes(this.value?.selectedModel as any),
                        value: this.value?.y,
                        onUpdate: x => this.update_value_field('y', x),
                    }),

                    DimensionSelector({
                        label: 'Z',
                        hidden: !['cube', 'cube_with_hole'].includes(this.value?.selectedModel as any),
                        value: this.value?.z,
                        onUpdate: x => this.update_value_field('z', x),
                    }),

                    DimensionSelector({
                        label: 'Height',
                        hidden: !['cylinder', 'cylinder_with_hole'].includes(this.value?.selectedModel as any),
                        value: this.value?.height,
                        onUpdate: x => this.update_value_field('height', x),
                    }),

                    DimensionSelector({
                        label: 'Diameter',
                        hidden: !['cylinder', 'cylinder_with_hole', 'circle'].includes(this.value?.selectedModel as any),
                        value: this.value?.diameter,
                        onUpdate: x => this.update_value_field('diameter', x),
                    }),
                ]),
            ]),

            FileNameSelector({
                hidden: this.value?.selectedModel === undefined || this.load_into,
                value: this.value?.file_name,
                onUpdate: (x) => this.update_value_field('file_name', x),
            }),

            h(SimpleSelectDocumentElement, {
                class: {
                    hidden:
                        !this.standalone
                        || !this.value?.selectedModel
                        || this.had_selected_document,
                },
                value: this.selected_document,
                'onUpdate:value': x => this.selected_document = x,
                api: this.api,
            }),

            h('section', {
                class: {
                    warning: true,
                    hidden: this.load_into || !this.value?.selectedModel || !this.bad_file_name,
                },
            }, 'The selected document name already exists.'),
        ]);
    },
});

export function ModelSelector(props: {
    value: Model | undefined;
    onUpdate: Unary<Model, void>;
}) {
    return h('section', {
        class: {
            models: true,
            [utility.round]: true,
            selected: props.value !== undefined,
            [utility.flexible]: true,
        },
    }, [
        h('div', {
            onClick: () => props.onUpdate('cube'),
            class: {
                active: props.value === 'cube',
            },
        }, h('img', {
            class: {
                loadimg: true,
            },
            id: 'cube',
            src: img_cube_png,
        })),

        h('div', {
            onClick: () => props.onUpdate('cube_with_hole'),
            class: {
                active: props.value === 'cube_with_hole',
            }
        }, h('img', {
            class: {
                loadimg: true,
            },
            id: 'cube_with_hole',
            src: img_cube_with_hole_png,
        })),

        h('div', {
            onClick: () => props.onUpdate('square'),
            class: {
                active: props.value === 'square'
            }
        }, h('img', {
            class: {
                loadimg: true,
            },
            id: 'square',
            src: img_square_png,
        })),

        h('div', {
            onClick: () => props.onUpdate('cylinder'),
            class: {
                active: props.value === 'cylinder'
            }
        }, h('img', {
            class: {
                loadimg: true,
            },
            id: 'cylinder',
            src: img_cylinder_png,
        })),

        h('div', {
            onClick: () => props.onUpdate('cylinder_with_hole'),
            class: {
                active: props.value === 'cylinder_with_hole'
            }
        }, h('img', {
            class: {
                loadimg: true,
            },
            id: 'cylinder_with_hole',
            src: img_cylinder_with_hole_png,
        })),

        h('div', {
            onClick: () => props.onUpdate('circle'),
            class: {
                active: props.value === 'circle'
            }
        }, h('img', {
            class: {
                loadimg: true,
            },
            id: 'circle',
            src: img_circle_png,
        })),
    ]);
}

export function UnitSelector(props: {
    value: Length | undefined;
    onUpdate: Unary<Length, void>;
}) {
    return h(Label, null, {
        label: () => h('span', 'Units:'),
        input: () => h(FormSelectUnits, {
            options: Object.values(Length).map(x => ({ text: x, value: x })),
            value: props.value,
            'onUpdate:value': props.onUpdate,
            class: 'w-4',
        }),
    });
}

export function DimensionSelector(props: {
    hidden: boolean;
    label: string;
    value: number | undefined;
    onUpdate: Unary<number, void>;
}) {
    return h(Label, {
        class: { hidden: props.hidden }
    }, {
        label: () => h('span', props.label + ':'),
        input: () => h(FormNumber, {
            value: props.value,
            change: true,
            'onUpdate:value': props.onUpdate,
            class: 'w-4',
            validator: PositiveNumber,
        }),
    });
}

const replace_bad_symbol = (x: string): string => disallowed_symbols.has(x) ? '_' : x;

const replace_bad_symbols = (x: string): string => Array.from(x).map(replace_bad_symbol).join('');

export const replace_bad_symbols_and_warn = (x: string): string => {
    const y = replace_bad_symbols(x);
    if (x !== y)
        display_message_to_user(
            LOG.WARN,
            'These characters are not allowed in file names: ' + join(disallowed_symbols)
        );
    return y;
};

export function FileNameSelector(props: {
    hidden: boolean;
    value: string | undefined;
    onUpdate: Unary<string, void>;
}) {
    return h(Label, {
        class: {
            hidden: props.hidden,
            'recommended-filename': true,
        },
    }, {
        label: () => h('span', 'File name:'),
        input: () => h(FormText, {
            id: 'recommended-filename',
            class: { hidden: props.hidden },
            value: props.value,
            'onUpdate:value': B(props.onUpdate)(replace_bad_symbols_and_warn),
            onKeydown: reject_symbols,
            change: true,
        }),
    });
}
